"use client";

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getDatabase, ref, onValue } from "firebase/database";
import {
  Home,
  Users,
  Package,
  ShoppingCart,
  MessageSquare,
  MessageCircle,
  Tag,
  Bell,
  AlertTriangle,
  Settings,
  LogOut,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Sun,
  Moon,
  Circle,
  Archive,
} from "lucide-react";
import "./admin-dashboard.css";
import { CountProvider, useCount } from "./contexts/count-context";
import UserSteelOrders from "./userSteelOrders";
import UserMachineryOrders from "./userMachineryOrders";
import SupplierManagement from "./admin-components/supplier-management";
import AdminAnalytics from "./admin-components/admin-analytics";
import SystemSettings from "./admin-components/system-settings";
import UserOrders from "../Component/user-orders/userOrders";
import UserQueries from "../Component/userQueries";
import UserQuotations from "../Component/userQuotations";
import RaisedIssues from "../Component/raisedIssue";
import NewOffers from "../Component/newOffers";
import BrandRates from "../Component/brandRates";
import StockAvailability from "../Component/stockAvailability";
import TransportGoodsReply from "./Transportation";
import ProductOffer from "../Component/productOffer";
import Finance from "./finance";
import SupplierNotifications from "../Component/notifications";
import ClientRecords from "../Component/client-records/clientRecords";
import AdminProductApproval from "../Component/admin-product-approval";
import DepotAccountForm from "./accountDetails";
import RefundPage from "./refunds";
import CurrentRates from "./current-rates";
import AltAlarm from "./altAlarm";
import Broadcast from "./broadcast";
import SupplierChatbot from "./chatbot";
import ClientDetails from "./admin-components/clientsOverview";
import SystemNotificationManager from "./systemNotificationManager";
import Help from "../Component/support";
import { db } from "../Firebase/firestore";
import AdminStockStatus from "./stockStatus";
import PaymentConfirmation from "./paymentConfirmation";
import AdminFirmRequests from "./AdminFirmRequests";
import Uploads from "../Component/client-records/clientRecords";

const AdminDashboardContent = () => {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState("welcome");
  const [previousView, setPreviousView] = useState("welcome");
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [totalQueries, setTotalQueries] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pendingIssues, setPendingIssues] = useState(0);
  const [docRequests, setDocRequests] = useState([]);
const [docRequestCount, setDocRequestCount] = useState(0);
  const [query, setQuery] = useState("");
  const [filteredPages, setFilteredPages] = useState([]);
  const searchRef = useRef(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [hasNewRateRequests, setHasNewRateRequests] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [isThemeLoaded, setIsThemeLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const adminEmail = localStorage.getItem("adminEmail") || "Admin";
  const [counts, setCounts] = useState({ chatbot: 0, groupChats: 0 });
  const { counts: navCounts } = useCount();
  const dropdownRef = useRef(null);
  const prevUnreadRef = useRef(0);

  // Total for Order Management section
  const orderManagementTotalCount =
    (navCounts.userOrders || 0) +
    (navCounts.userSteelOrders || 0) +
    (navCounts.userMachineryOrders || 0) +
    (navCounts.transportGoods || 0) +
    (navCounts.currentRates || 0) +
    (navCounts.refunds || 0);

  // Total for Client Interactions section
  const clientIntractionsTotalCount =
    (navCounts.userQueries || 0) +
    (navCounts.userQuotations || 0) +
    (counts.chatbot || 0) +
    (counts.groupChats || 0);

  // Dropdown toggle handler
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    const groupChatsRef = ref(db, "groupChats");
    const unsubscribeGroup = onValue(groupChatsRef, (snapshot) => {
      let groupUnread = 0;
      if (snapshot.exists()) {
        const data = snapshot.val();
        Object.values(data).forEach((groupData) => {
          const messages = groupData.messages || {};
          Object.values(messages).forEach((msg) => {
            if (msg && msg.sender === "user" && msg.read !== true) {
              groupUnread++;
            }
          });
        });
      }
      setCounts((prev) => ({ ...prev, groupChats: groupUnread }));
    });

    return () => unsubscribeGroup();
  }, []);

  useEffect(() => {
    const chatsRef = ref(db, "userchatbox");
    const unsubscribeIndividual = onValue(chatsRef, (snapshot) => {
      let individualUnread = 0;
      if (snapshot.exists()) {
        const data = snapshot.val();
        Object.values(data).forEach((userChats) => {
          Object.values(userChats).forEach((chatData) => {
            if (!chatData || typeof chatData !== "object") return;

            const lastRead = chatData.lastReadBySupplier || 0;

            Object.entries(chatData).forEach(([key, msg]) => {
              if (
                key === "companyName" ||
                key === "chatStatus" ||
                key === "lastReadBySupplier"
              )
                return;

              if (msg && msg.sender === "user") {
                const msgTime = Number(msg.timestamp) || 0;

                if (msgTime > lastRead) {
                  individualUnread++;
                }
              }
            });
          });
        });
      }
      setCounts((prev) => ({ ...prev, chatbot: individualUnread }));
    });

    return () => unsubscribeIndividual();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const initializeTheme = () => {
      try {
        const savedTheme = localStorage.getItem("admin-theme");
        let isDark = false;
        if (savedTheme) {
          isDark = savedTheme === "dark";
        } else {
          const prefersDark = window.matchMedia(
            "(prefers-color-scheme: dark)",
          ).matches;
          isDark = prefersDark;
          localStorage.setItem("admin-theme", isDark ? "dark" : "light");
        }
        setDarkMode(isDark);
        applyThemeToDocument(isDark);
        setIsThemeLoaded(true);
      } catch (error) {
        console.error("Error initializing theme:", error);
        setDarkMode(false);
        applyThemeToDocument(false);
        setIsThemeLoaded(true);
      }
    };
    initializeTheme();
  }, []);

  const applyThemeToDocument = (isDark) => {
    try {
      const body = document.body;
      const html = document.documentElement;
      if (isDark) {
        body.classList.add("dark-theme");
        body.classList.remove("light-theme");
        html.classList.add("dark-theme");
        html.classList.remove("light-theme");
      } else {
        body.classList.add("light-theme");
        body.classList.remove("dark-theme");
        html.classList.add("light-theme");
        html.classList.remove("dark-theme");
      }
    } catch (error) {
      console.error("Error applying theme to document:", error);
    }
  };

  useEffect(() => {
    if (isThemeLoaded) {
      try {
        localStorage.setItem("admin-theme", darkMode ? "dark" : "light");
        applyThemeToDocument(darkMode);
      } catch (error) {
        console.error("Error updating theme:", error);
      }
    }
  }, [darkMode, isThemeLoaded]);

  const toggleTheme = () => {
    try {
      const newTheme = !darkMode;
      setDarkMode(newTheme);
      applyThemeToDocument(newTheme);
      localStorage.setItem("admin-theme", newTheme ? "dark" : "light");
      const button = document.querySelector(".admin-theme-toggle");
      if (button) {
        button.style.transform = "scale(0.95)";
        setTimeout(() => {
          button.style.transform = "scale(1)";
        }, 150);
      }
    } catch (error) {
      console.error("Error toggling theme:", error);
    }
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemThemeChange = (e) => {
      const savedTheme = localStorage.getItem("admin-theme");
      if (!savedTheme) {
        setDarkMode(e.matches);
        applyThemeToDocument(e.matches);
      }
    };
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleSystemThemeChange);
    } else {
      mediaQuery.addListener(handleSystemThemeChange);
    }
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleSystemThemeChange);
      } else {
        mediaQuery.removeListener(handleSystemThemeChange);
      }
    };
  }, []);

  useEffect(() => {
    const isAdmin = localStorage.getItem("isAdmin") === "true";
    if (!isAdmin) {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const fetchTotalQueries = () => {
      const db = getDatabase();
      const queriesRef = ref(db, "queries");
      onValue(queriesRef, (snapshot) => {
        if (snapshot.exists()) {
          const queriesData = snapshot.val();
          let totalQueriesCount = 0;
          Object.values(queriesData).forEach((userQueries) => {
            totalQueriesCount += Object.keys(userQueries).length;
          });
          setTotalQueries(totalQueriesCount);
        } else {
          setTotalQueries(0);
        }
      });
    };
    fetchTotalQueries();
  }, []);

  useEffect(() => {
    const fetchTotalOrders = async () => {
      try {
        const db = getDatabase();
        const ordersRef = ref(db, "orders");
        onValue(ordersRef, (snapshot) => {
          if (snapshot.exists()) {
            const ordersData = snapshot.val();
            let count = 0;
            Object.keys(ordersData).forEach((userId) => {
              count += Object.keys(ordersData[userId]).length;
            });
            setTotalOrders(count);
          } else {
            setTotalOrders(0);
          }
        });
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
    };
    fetchTotalOrders();
  }, []);

  useEffect(() => {
    const fetchTotalUsers = async () => {
      try {
        const db = getDatabase();
        const usersRef = ref(db, "profileDashboard");
        onValue(usersRef, (snapshot) => {
          if (snapshot.exists()) {
            const usersData = snapshot.val();
            setTotalUsers(Object.keys(usersData).length);
          } else {
            setTotalUsers(0);
          }
        });
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchTotalUsers();
  }, []);

  useEffect(() => {
    const fetchPendingIssues = async () => {
      try {
        const db = getDatabase();
        const issuesRef = ref(db, "issues");
        onValue(issuesRef, (snapshot) => {
          if (snapshot.exists()) {
            const issuesData = snapshot.val();
            let count = 0;
            Object.keys(issuesData).forEach((userId) => {
              Object.values(issuesData[userId]).forEach((issue) => {
                if (issue.status === "Pending") {
                  count++;
                }
              });
            });
            setPendingIssues(count);
          } else {
            setPendingIssues(0);
          }
        });
      } catch (error) {
        console.error("Error fetching issues:", error);
      }
    };
    fetchPendingIssues();
  }, []);

  useEffect(() => {
  const dbInstance = getDatabase();
  const docRef = ref(dbInstance, "document_requests");

  const unsubscribe = onValue(docRef, (snapshot) => {
    if (!snapshot.exists()) {
      setDocRequests([]);
      setDocRequestCount(0);
      return;
    }

    const data = snapshot.val();
    const all = [];

    Object.keys(data).forEach((uid) => {
      const userNode = data[uid];
      if (!userNode) return;

      Object.keys(userNode).forEach((type) => {
        const typeNode = userNode[type];
        if (!typeNode) return;

        Object.keys(typeNode).forEach((id) => {
          const item = typeNode[id];

          if (item && item.status === "pending") {
            all.push({
              ...item,
              type,
            });
          }
        });
      });
    });

    // sort latest first
    all.sort((a, b) =>
      (b.timestamp || "").localeCompare(a.timestamp || "")
    );

    setDocRequests(all.slice(0, 5));
    setDocRequestCount(all.length);
  });

  return () => unsubscribe();
}, []);

  useEffect(() => {
    const fetchUnreadNotifications = async () => {
      try {
        const db = getDatabase();
        const types = [
          "Steel Orders",
          "New Query",
          "Negotiations",
          "New Orders",
          "Quotations",
          "Raised Issues",
          "Machinery Orders",
          "transportGoods",
          "Transport Request",
          "Stop_Order",
          "Refund",
          "finance",
          "StockStatus",
          "PaymentProofAdded",
          "firm_requests",
        ];
        const allNotifications = [];
        const now = Date.now();
        const threshold = 48 * 60 * 60 * 1000;
        const promises = types.map((type) => {
          const notificationsRef = ref(db, `supplier_notifications/${type}`);
          return new Promise((resolve) => {
            onValue(
              notificationsRef,
              (snapshot) => {
                if (snapshot.exists()) {
                  const data = snapshot.val();
                  Object.keys(data).forEach((userId) => {
                    Object.entries(data[userId]).forEach(
                      ([notifId, notification]) => {
                        const notificationTime = new Date(
                          notification.timestamp,
                        ).getTime();
                        const isWithin48Hours =
                          now - notificationTime < threshold;
                        if (isWithin48Hours) {
                          allNotifications.push({
                            ...notification,
                            id: notifId,
                            type,
                            userId,
                          });
                        }
                      },
                    );
                  });
                }
                resolve();
              },
              { onlyOnce: true },
            );
          });
        });
        await Promise.all(promises);
        allNotifications.sort(
          (a, b) => (b.timestamp || 0) - (a.timestamp || 0),
        );
        const unreadCount = allNotifications.filter((n) => !n.seen).length;

        // Auto open dropdown if new notification arrives
        if (unreadCount > prevUnreadRef.current) {
          setShowNotifications(true);
        }

        prevUnreadRef.current = unreadCount;
        setUnreadNotifications(unreadCount);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchUnreadNotifications();
    window.decreaseUnreadCount = () => {
      setUnreadNotifications((prev) => Math.max(0, prev - 1));
    };

    const refreshInterval = setInterval(fetchUnreadNotifications, 1000);
    return () => {
      clearInterval(refreshInterval);
      delete window.decreaseUnreadCount;
    };
  }, []);

  useEffect(() => {
    const db = getDatabase();
    const rateRequestsRef = ref(db, "rateRequest");
    const unsubscribe = onValue(rateRequestsRef, (snapshot) => {
      let hasNew = false;
      if (snapshot.exists()) {
        const data = snapshot.val();
        Object.keys(data).forEach((userId) => {
          Object.values(data[userId]).forEach((request) => {
            if (!request.rate && !request.seen) {
              hasNew = true;
            }
          });
        });
      }
      setHasNewRateRequests(hasNew);
    });
    return () => unsubscribe();
  }, []);

  const toggleSubmenu = (menu) => {
    if (expandedMenu === menu) {
      setExpandedMenu(null);
    } else {
      setExpandedMenu(menu);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  const pages = [
    { name: "dashboard", path: "welcome" },
    { name: "suppliers", path: "suppliers" },
    { name: "analytics", path: "analytics" },
    { name: "cement orders", path: "userOrders" },
    { name: "quotations", path: "userQuotations" },
    { name: "queries", path: "userQueries" },
    { name: "issues", path: "raisedIssue" },
    { name: "offers", path: "newOffers" },
    { name: "products", path: "productOffer" },
    { name: "rates", path: "brandRates" },
    { name: "current rates", path: "currentRates" },
    { name: "stock", path: "stockAvailability" },
    { name: "settings", path: "settings" },
    { name: "finance", path: "finance" },
    { name: "steel orders", path: "userSteelOrders" },
    { name: "machinery orders", path: "userMachineryOrders" },
    { name: "transport goods", path: "transport-goods-reply" },
    { name: "refunds", path: "refunds" },
    { name: "ultratech cement orders", path: "userOrders" },
    { name: "shree cement orders", path: "userOrders" },
    { name: "jksuper cement orders", path: "userOrders" },
    { name: "JSW cement orders", path: "userOrders" },
    { name: "pending orders", path: "userOrders" },
    { name: "Underloading orders", path: "userOrders" },
    { name: "Dispatch orders", path: "userOrders" },
    { name: "completed orders", path: "userOrders" },
    { name: "broadcasting", path: "broadcast" },
    { name: "ambuja cement orders", path: "userOrders" },
    { name: "alt alarm", path: "altAlarm" },
    { name: "ACC cement orders", path: "userOrders" },
    { name: "client overview", path: "clientsOverview" },
  ];

  const handleSearch = (e) => {
    const searchText = e.target.value;
    const searchQuery = searchText.toLowerCase().trim();
    setQuery(searchText); // keep input exact (with spaces etc.)

    if (searchQuery) {
      const results = pages.filter((page) => {
        const name = page.name.toLowerCase().trim();

        return (
          name.startsWith(searchQuery) || name.split(" ").includes(searchQuery)
        );
      });

      const uniqueResults = results.filter(
        (page, index, self) =>
          index ===
          self.findIndex(
            (p) =>
              p.name.toLowerCase().trim() === page.name.toLowerCase().trim(),
          ),
      );

      setFilteredPages(uniqueResults);
    } else {
      setFilteredPages([]);
    }
  };

  // ✅ Space key allowed
  const handleKeyDown = (e) => {
    if (e.key === " " || e.keyCode === 32) {
      e.stopPropagation();
    }
  };

  const handleNavigate = (path) => {
    setQuery("");
    setFilteredPages([]);
    setIsMobileMenuOpen(false);
    if (path === "chatbot") {
      if (currentView === "chatbot") {
        setCurrentView(previousView);
      } else {
        setPreviousView(currentView);
        setCurrentView(path);
      }
    } else {
      setCurrentView(path);
    }
  };

  const handleSupplierChange = (e) => {
    const brand = e.target.value;
    setSelectedSupplier(brand);
    if (brand) {
      localStorage.setItem("adminSelectedBrand", brand);
    } else {
      localStorage.removeItem("adminSelectedBrand");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("userToken");
    localStorage.removeItem("isAdmin");
    localStorage.removeItem("adminEmail");
    localStorage.removeItem("adminSelectedBrand");
    localStorage.removeItem("supplierBrand");
    navigate("/login", { replace: true });
  };

  const predefinedBrands = [
    "Ultratech",
    "Shree",
    "JSW",
    "JKSuper",
    "ACC",
    "Ambuja",
  ];

  useEffect(() => {
    const handleNotificationClick = (event) => {
      const { view, notificationId, orderType, brand } = event.detail;
      setCurrentView(view);
      if (brand) {
        setSelectedSupplier(brand);
        localStorage.setItem("adminSelectedBrand", brand);
      }
      if (orderType) {
        localStorage.setItem("filterStatus", orderType);
      }
      setShowNotifications(false);
    };
    window.addEventListener("changeView", handleNotificationClick);
    return () => {
      window.removeEventListener("changeView", handleNotificationClick);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const notificationsPanel = document.querySelector(
        ".admin-notifications-dropdown",
      );
      const notificationsButton = document.querySelector(
        ".admin-notifications-btn",
      );
      if (
        notificationsPanel &&
        !notificationsPanel.contains(event.target) &&
        notificationsButton &&
        !notificationsButton.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (!isThemeLoaded) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: darkMode ? "#0f172a" : "#f8fafc",
          color: darkMode ? "#f8fafc" : "#1e293b",
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <div
      className={`admin-dashboard-container ${darkMode ? "dark-theme" : "light-theme"}`}
    >
      <header className="admin-navbar">
        <div className="admin-navbar-container">
          <button
            className="admin-mobile-toggle"
            type="button"
            onClick={toggleMobileMenu}
            aria-controls="navbarContent"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <Menu size={24} />
          </button>
          <div
            className="admin-navbar-brand"
            onClick={() => setCurrentView("welcome")}
          >
            <div className="admin-brand-logo">
              <Package size={24} />
            </div>
            <span className="admin-brand-text">Admin Dashboard</span>
          </div>
          <div className="admin-desktop-search" ref={searchRef}>
            <div className="admin-search-input-group">
              <input
                type="text"
                value={query}
                onChange={handleSearch}
                placeholder="Search pages..."
                maxLength={46}
                className="admin-search-input"
              />
            </div>
            {query && (
              <ul className="admin-search-dropdown">
                {filteredPages.length > 0 ? (
                  filteredPages.map((page) => (
                    <li key={page.path}>
                      <button
                        className="admin-dropdown-item"
                        onClick={() => handleNavigate(page.path)}
                      >
                        {page.name}
                      </button>
                    </li>
                  ))
                ) : (
                  <li>
                    <span className="admin-dropdown-item-disabled">
                      No results found
                    </span>
                  </li>
                )}
              </ul>
            )}
          </div>
          {currentView !== "welcome" &&
            currentView !== "suppliers" &&
            currentView !== "analytics" &&
            currentView !== "settings" && (
              <div className="admin-supplier-selector">
                <select
                  value={selectedSupplier}
                  onChange={handleSupplierChange}
                  className="admin-supplier-select"
                >
                  <option value="">All Suppliers</option>
                  {predefinedBrands.map((brand) => (
                    <option key={brand} value={brand}>
                      {brand}
                    </option>
                  ))}
                  {suppliers
                    .filter(
                      (supplier) => !predefinedBrands.includes(supplier.brand),
                    )
                    .map((supplier) => (
                      <option key={supplier.id} value={supplier.brand}>
                        {supplier.brand}
                      </option>
                    ))}
                </select>
              </div>
            )}
          <div className="admin-navbar-actions">
            <button
              className="admin-theme-toggle"
              onClick={toggleTheme}
              aria-label={`Switch to ${darkMode ? "light" : "dark"} theme`}
              title={`Switch to ${darkMode ? "light" : "dark"} theme`}
            >
              {darkMode ? <Sun size={22} /> : <Moon size={22} />}
            </button>
            <div
              className="admin-rates-bar"
              onClick={() => {
                setHasNewRateRequests(false);
                handleNavigate("currentRates");
              }}
              title="Current Rates"
            >
              <span className="admin-rates-icon">₹</span>
              <span className="admin-rates-label">Rate Requests</span>
              {hasNewRateRequests && (
                <span className="admin-rates-badge"></span>
              )}
            </div>
            <div className="admin-notifications-wrapper">
              <button
                className="admin-notifications-btn"
                onClick={toggleNotifications}
              >
                <Bell size={25} />
                {unreadNotifications > 0 && (
                  <span className="admin-notifications-badge">
                    {unreadNotifications}
                  </span>
                )}
              </button>
              {showNotifications && (
                <div
                  className="admin-notifications-overlay"
                  onClick={toggleNotifications}
                ></div>
              )}
              {showNotifications && (
                <div className="admin-notifications-dropdown">
                  <SupplierNotifications adminView={true} />
                </div>
              )}
            </div>
            <div className="admin-profile-wrapper" ref={dropdownRef}>
              <div
                className="admin-profile-image-container"
                onClick={toggleDropdown}
              >
                <img
                  src="/Images/profile103.png"
                  alt="Admin Profile"
                  className="admin-profile-image"
                  width="35"
                  height="35"
                />
              </div>
              {isOpen && (
                <div className="admin-profile-dropdown">
                  <span className="admin-email">{adminEmail}</span>
                  <button
                    className="admin-sidebar-logout-btn"
                    onClick={handleLogout}
                  >
                    <LogOut size={20} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      <div
        className="admin-chat-fab"
        onClick={() => handleNavigate("chatbot")}
        title="Open Chatbot"
      >
        <MessageCircle size={28} />
        {counts.chatbot + counts.groupChats > 0 && (
          <span className="admin-chat-badge">
            {counts.chatbot + counts.groupChats}
          </span>
        )}
      </div>
      <div className="admin-dashboard-main">
        <div
          className={`admin-sidebar-overlay ${isMobileMenuOpen ? "show" : ""}`}
          onClick={toggleMobileMenu}
        ></div>
        <aside className={`admin-sidebar ${isMobileMenuOpen ? "show" : ""}`}>
          <div className="admin-sidebar-header">
            <h5>Navigation Menu</h5>
            <button className="admin-close-btn" onClick={toggleMobileMenu}>
              <X size={24} />
            </button>
          </div>
          <div className="admin-sidebar-menu">
            <div
              className="admin-menu-item"
              onClick={() => handleNavigate("welcome")}
            >
              <div
                className={`admin-menu-link ${currentView === "welcome" ? "active" : ""}`}
              >
                <Home size={18} />
                <span>Dashboard</span>
              </div>
            </div>
            <div
              className="admin-menu-item"
              onClick={() => handleNavigate("clientRecords")}
            >
              <div
                className={`admin-menu-link ${currentView === "clientRecords" ? "active" : ""}`}
              >
                <Users size={18} />
                <span>Clients Record</span>
              </div>
            </div>
            <div className="admin-menu-group">
              <div
                className={`admin-menu-item ${expandedMenu === "orders" ? "expanded" : ""}`}
                onClick={() => toggleSubmenu("orders")}
              >
                <div className="admin-menu-link">
                  <ShoppingCart size={18} />
                  <span>Order Management</span>
                  {orderManagementTotalCount > 0 && (
                    <span className="admin-menu-badge">
                      {orderManagementTotalCount}
                    </span>
                  )}
                  {expandedMenu === "orders" ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </div>
              </div>
              {expandedMenu === "orders" && (
                <div className="admin-submenu">
                  <div
                    className={`admin-submenu-item ${currentView === "userOrders" ? "active" : ""}`}
                    onClick={() => handleNavigate("userOrders")}
                  >
                    <span>Cement Orders</span>
                    {navCounts.userOrders > 0 && (
                      <span className="admin-menu-badge">
                        {navCounts.userOrders}
                      </span>
                    )}
                  </div>
                  <div
                    className={`admin-submenu-item ${currentView === "userSteelOrders" ? "active" : ""}`}
                    onClick={() => handleNavigate("userSteelOrders")}
                  >
                    <span>Steel Orders</span>
                    {navCounts.userSteelOrders > 0 && (
                      <span className="admin-menu-badge">
                        {navCounts.userSteelOrders}
                      </span>
                    )}
                  </div>
                  <div
                    className={`admin-submenu-item ${currentView === "userMachineryOrders" ? "active" : ""}`}
                    onClick={() => handleNavigate("userMachineryOrders")}
                  >
                    <span>Machinery Orders</span>
                    {navCounts.userMachineryOrders > 0 && (
                      <span className="admin-menu-badge">
                        {navCounts.userMachineryOrders}
                      </span>
                    )}
                  </div>
                  <div
                    className={`admin-submenu-item ${currentView === "stockAvailability" ? "active" : ""}`}
                    onClick={() => handleNavigate("stockAvailability")}
                  >
                    <span>Stock Availability</span>
                  </div>
                  <div
                    className={`admin-submenu-item ${currentView === "transport-goods-reply" ? "active" : ""}`}
                    onClick={() => handleNavigate("transport-goods-reply")}
                  >
                    <span>Transport Goods</span>
                    {navCounts.transportGoods > 0 && (
                      <span className="admin-menu-badge">
                        {navCounts.transportGoods}
                      </span>
                    )}
                  </div>
                  <div
                    className={`admin-submenu-item ${currentView === "currentRates" ? "active" : ""}`}
                    onClick={() => handleNavigate("currentRates")}
                  >
                    <span>Current Rates</span>
                    {hasNewRateRequests && (
                      <span className="admin-menu-badge"></span>
                    )}
                    {navCounts.currentRates > 0 && (
                      <span className="admin-menu-badge">
                        {navCounts.currentRates}
                      </span>
                    )}
                  </div>
                  <div
                    className={`admin-submenu-item ${currentView === "refunds" ? "active" : ""}`}
                    onClick={() => handleNavigate("refunds")}
                  >
                    <span>Refund Requests</span>
                    {navCounts.refunds > 0 && (
                      <span className="admin-menu-badge">
                        {navCounts.refunds}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* Approval Requests menu item - added before Finance
<div className="admin-menu-group">
  <div
    className={`admin-submenu-item ${currentView === "approvalRequests" ? "active" : ""}`}
    onClick={() => handleNavigate("approvalRequests")}
  >
    <div className="admin-menu-link">
      <Tag size={18} />
      <span>Partner Requests</span>
    </div>
  </div>
</div> */}
            <div className="admin-menu-group">
              <div
                className={`admin-submenu-item ${currentView === "AdminFirmRequests" ? "active" : ""}`}
                onClick={() => handleNavigate("AdminFirmRequests")}
              >
                <div className="admin-menu-link">
                  <Tag size={18} />
                  <span>Firm Requests</span>
                </div>
              </div>
            </div>
            <div className="admin-menu-group">
              <div
                className={`admin-menu-item ${expandedMenu === "communication" ? "expanded" : ""}`}
                onClick={() => toggleSubmenu("communication")}
              >
                <div className="admin-menu-link">
                  <MessageSquare size={18} />
                  <span>Client Interaction</span>
                  {clientIntractionsTotalCount > 0 && (
                    <span className="admin-menu-badge">
                      {clientIntractionsTotalCount}
                    </span>
                  )}
                  {expandedMenu === "communication" ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </div>
              </div>
              {expandedMenu === "communication" && (
                <div className="admin-submenu">
                  <div
                    className={`admin-submenu-item ${currentView === "userQueries" ? "active" : ""}`}
                    onClick={() => handleNavigate("userQueries")}
                  >
                    <span>User Queries</span>
                    {navCounts.userQueries > 0 && (
                      <span className="admin-menu-badge">
                        {navCounts.userQueries}
                      </span>
                    )}
                  </div>
                  <div
                    className={`admin-submenu-item ${currentView === "userQuotations" ? "active" : ""}`}
                    onClick={() => handleNavigate("userQuotations")}
                  >
                    <span>User Quotations</span>
                    {navCounts.userQuotations > 0 && (
                      <span className="admin-menu-badge">
                        {navCounts.userQuotations}
                      </span>
                    )}
                  </div>
                  <div
                    className={`admin-submenu-item ${currentView === "broadcast" ? "active" : ""}`}
                    onClick={() => handleNavigate("broadcast")}
                  >
                    <span>Broadcasting</span>
                  </div>
                  <div
                    className={`admin-submenu-item ${currentView === "altAlarm" ? "active" : ""}`}
                    onClick={() => handleNavigate("altAlarm")}
                  >
                    <span>Alt Alarm</span>
                  </div>
                </div>
              )}
            </div>
            <div className="admin-menu-group">
              <div
                className={`admin-menu-item ${expandedMenu === "marketing" ? "expanded" : ""}`}
                onClick={() => toggleSubmenu("marketing")}
              >
                <div className="admin-menu-link">
                  <Tag size={18} />
                  <span>Product & Pricing</span>
                  {expandedMenu === "marketing" ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </div>
              </div>
              {expandedMenu === "marketing" && (
                <div className="admin-submenu">
                  <div
                    className={`admin-submenu-item ${currentView === "newOffers" ? "active" : ""}`}
                    onClick={() => handleNavigate("newOffers")}
                  >
                    <span>New Offers</span>
                  </div>
                  <div
                    className={`admin-submenu-item ${currentView === "productOffer" ? "active" : ""}`}
                    onClick={() => handleNavigate("productOffer")}
                  >
                    <span>New Product</span>
                  </div>
                  <div
                    className={`admin-submenu-item ${currentView === "productApproval" ? "active" : ""}`}
                    onClick={() => handleNavigate("productApproval")}
                  >
                    <span>Product Approval</span>
                  </div>
                  <div
                    className={`admin-submenu-item ${currentView === "brandRates" ? "active" : ""}`}
                    onClick={() => handleNavigate("brandRates")}
                  >
                    <span>Brand Rates</span>
                  </div>
                </div>
              )}
            </div>

            <div className="admin-menu-group">
              <div
                className="admin-menu-item"
                onClick={() => handleNavigate("finance")}
              >
                <div className="admin-menu-link">
                  <Tag size={18} />
                  <span>Finance</span>

                  {navCounts.finance > 0 && (
                    <span className="admin-menu-badge">
                      {navCounts.finance}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div
              className="admin-menu-item"
              onClick={() => handleNavigate("raisedIssue")}
            >
              <div
                className={`admin-menu-link ${currentView === "raisedIssue" ? "active" : ""}`}
              >
                <Bell size={18} />
                <span>Issues</span>
                {pendingIssues > 0 && (
                  <span className="admin-badge">{pendingIssues}</span>
                )}
              </div>
            </div>
            <div
              className="admin-menu-item"
              onClick={() => handleNavigate("stockStatus")}
            >
              <div
                className={`admin-menu-link ${currentView === "stockStatus" ? "active" : ""}`}
              >
                <Package size={18} />
                <span>Stock Status</span>
                {AdminStockStatus > 0 && (
                  <span className="admin-badge">{AdminStockStatus}</span>
                )}
              </div>
            </div>
            <div
              className="admin-menu-item"
              onClick={() => handleNavigate("clientsOverview")}
            >
              <div
                className={`admin-menu-link ${currentView === "clientsOverview" ? "active" : ""}`}
              >
                <Users size={18} />
                <span>Clients Overview</span>
              </div>
            </div>
            <div
              className="admin-menu-item"
              onClick={() => handleNavigate("analytics")}
            >
              <div
                className={`admin-menu-link ${currentView === "analytics" ? "active" : ""}`}
              >
                <Package size={18} />
                <span>Analytics</span>
              </div>
            </div>
            <div
              className="admin-menu-item"
              onClick={() => handleNavigate("support")}
            >
              <div
                className={`admin-menu-link ${currentView === "support" ? "active" : ""}`}
              >
                <Bell size={18} />
                <span>Support Requests</span>
              </div>
            </div>
          </div>
        </aside>
        <section className="admin-main-content">
          <div className="admin-content-container">
            {currentView === "welcome" && (
              <div className="admin-welcome-dashboard">
                <div className="admin-welcome-header">
                  <h1>Welcome to Admin Dashboard</h1>
                  <p>
                    Comprehensive management system for all business operations
                  </p>
                </div>
                <div className="admin-stats-grid">
                  <div className="admin-stat-card">
                    <div className="admin-stat-icon bg-primary-light">
                      <Users size={24} />
                    </div>
                    <div className="admin-stat-content">
                      <h3>{totalQueries}</h3>
                      <p>Total Queries</p>
                      <div className="admin-stat-trend">
                        +12% from last month
                      </div>
                    </div>
                  </div>
                  <div className="admin-stat-card">
                    <div className="admin-stat-icon bg-success-light">
                      <ShoppingCart size={24} />
                    </div>
                    <div className="admin-stat-content">
                      <h3>{totalOrders}</h3>
                      <p>Total Orders</p>
                      <div className="admin-stat-trend">
                        +8% from last month
                      </div>
                    </div>
                  </div>
                  <div className="admin-stat-card">
                    <div className="admin-stat-icon bg-warning-light">
                      <Users size={24} />
                    </div>
                    <div className="admin-stat-content">
                      <h3>{totalUsers}</h3>
                      <p>Registered Users</p>
                      <div className="admin-stat-trend">
                        +15% from last month
                      </div>
                    </div>
                  </div>
                  <div className="admin-stat-card">
                    <div className="admin-stat-icon bg-danger-light">
                      <Bell size={24} />
                    </div>
                    <div className="admin-stat-content">
                      <h3>{pendingIssues}</h3>
                      <p>Pending Issues</p>
                      <div className="admin-stat-trend">Requires attention</div>
                    </div>
                  </div>
                </div>
                <div
                  className="admin-doc-card"
                  onClick={() => handleNavigate("uploads")}
                >
                  <div className="admin-doc-header">
                    <span className="admin-doc-title">Docs Requested</span>
                    <span className="admin-doc-count">{docRequestCount}</span>
                  </div>

                  <div className="admin-doc-list">
                    {docRequests.map((req, idx) => (
                      <div key={idx} className="admin-doc-item">
                        <div>
                          <span className="admin-doc-company">
                            {req.companyName}
                          </span>
                          <span className="admin-doc-type">{req.type}</span>
                        </div>

                        <div className="admin-doc-details">{req.details}</div>

                        <div className="admin-doc-date">{req.timestamp}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="admin-quick-actions">
                  <h2>Quick Actions</h2>
                  <div className="admin-actions-grid">
                    <button
                      className="admin-action-button"
                      onClick={() => handleNavigate("refunds")}
                    >
                      <MessageSquare size={20} />
                      <div className="admin-action-content">
                        <span>Refund Requests</span>
                        <small>Manage customer refunds</small>
                      </div>
                    </button>
                    <button
                      className="admin-action-button"
                      onClick={() => handleNavigate("userOrders")}
                    >
                      <ShoppingCart size={20} />
                      <div className="admin-action-content">
                        <span>View Orders</span>
                        <small>Monitor all orders</small>
                      </div>
                    </button>
                    <button
                      className="admin-action-button"
                      onClick={() => handleNavigate("raisedIssue")}
                    >
                      <Bell size={20} />
                      <div className="admin-action-content">
                        <span>Handle Issues</span>
                        <small>Resolve customer issues</small>
                      </div>
                    </button>
                    <button
                      className="admin-action-button"
                      onClick={() => handleNavigate("analytics")}
                    >
                      <Package size={20} />
                      <div className="admin-action-content">
                        <span>View Analytics</span>
                        <small>Business insights</small>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            )}
            {currentView === "suppliers" && <SupplierManagement />}
            {currentView === "analytics" && <AdminAnalytics />}
            {currentView === "settings" && <SystemSettings />}
            {currentView === "userOrders" && (
              <UserOrders adminView={true} selectedBrand={selectedSupplier} />
            )}
            {currentView === "userQueries" && (
              <UserQueries adminView={true} selectedBrand={selectedSupplier} />
            )}
            {currentView === "userQuotations" && (
              <UserQuotations
                adminView={true}
                selectedBrand={selectedSupplier}
              />
            )}
            {currentView === "raisedIssue" && (
              <RaisedIssues adminView={true} selectedBrand={selectedSupplier} />
            )}
            {currentView === "newOffers" && (
              <NewOffers adminView={true} selectedBrand={selectedSupplier} />
            )}
            {currentView === "AdminFirmRequests" && <AdminFirmRequests />}
            {currentView === "finance" && (
              <Finance adminView={true} selectedBrand={selectedSupplier} />
            )}
            {currentView === "productOffer" && (
              <ProductOffer adminView={true} selectedBrand={selectedSupplier} />
            )}
            {currentView === "brandRates" && (
              <BrandRates adminView={true} selectedBrand={selectedSupplier} />
            )}
            {currentView === "altAlarm" && (
              <AltAlarm adminView={true} selectedBrand={selectedSupplier} />
            )}
            {currentView === "accountDetails" && (
              <DepotAccountForm
                adminView={true}
                selectedBrand={selectedSupplier}
              />
            )}
            {currentView === "refunds" && (
              <RefundPage adminView={true} selectedBrand={selectedSupplier} />
            )}
            {currentView === "clientRecords" && (
              <ClientRecords
                adminView={true}
                selectedBrand={selectedSupplier}
              />
            )}
            {currentView === "userSteelOrders" && (
              <UserSteelOrders
                adminView={true}
                selectedBrand={selectedSupplier}
              />
            )}
            {currentView === "broadcast" && (
              <Broadcast adminView={true} selectedBrand={selectedSupplier} />
            )}
            {currentView === "chatbot" && (
              <SupplierChatbot
                adminView={true}
                selectedBrand={selectedSupplier}
              />
            )}
            {currentView === "support" && (
              <Help adminView={true} selectedBrand={selectedSupplier} />
            )}
            {currentView === "paymentConfirmation" && (
              <PaymentConfirmation
                adminView={true}
                selectedBrand={selectedSupplier}
              />
            )}
            {currentView === "userMachineryOrders" && (
              <UserMachineryOrders
                adminView={true}
                selectedBrand={selectedSupplier}
              />
            )}
            {currentView === "stockAvailability" && (
              <StockAvailability
                adminView={true}
                selectedBrand={selectedSupplier}
              />
            )}
            {currentView === "transport-goods-reply" && (
              <TransportGoodsReply
                adminView={true}
                selectedBrand={selectedSupplier}
              />
            )}
            {currentView === "stockStatus" && (
              <AdminStockStatus
                adminView={true}
                selectedBrand={selectedSupplier}
              />
            )}
            {currentView === "productApproval" && (
              <AdminProductApproval selectedBrand={selectedSupplier} />
            )}
            {currentView === "currentRates" && (
              <CurrentRates adminView={true} selectedBrand={selectedSupplier} />
            )}
            {currentView === "clientsOverview" && (
              <ClientDetails
                adminView={true}
                selectedBrand={selectedSupplier}
              />
            )}
            {currentView === "uploads" && (
              <Uploads adminView={true} selectedBrand={selectedSupplier} />
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [isOnDashboard, setIsOnDashboard] = useState(true);
  const [currentView, setCurrentView] = useState("welcome");
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsOnDashboard(!document.hidden);
    };

    const handleFocus = () => setIsOnDashboard(true);
    const handleBlur = () => setIsOnDashboard(false);

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  useEffect(() => {
    const handleSystemNotificationClick = (event) => {
      const { type, notificationId } = event.detail;
      if (
        type === "New Orders" ||
        type === "Steel Orders" ||
        type === "Machinery Orders"
      ) {
        setCurrentView("userOrders");
      } else if (type === "Raised Issues") {
        setCurrentView("raisedIssue");
      } else if (type === "New Query" || type === "Negotiations") {
        setCurrentView("userQueries");
      } else if (type === "Quotations") {
        setCurrentView("userQuotations");
      }
      setShowNotifications(false);
    };

    window.addEventListener(
      "systemNotificationClick",
      handleSystemNotificationClick,
    );
    return () => {
      window.removeEventListener(
        "systemNotificationClick",
        handleSystemNotificationClick,
      );
    };
  }, []);

  return (
    <div className="admin-dashboard">
      <SystemNotificationManager isOnDashboard={isOnDashboard} />
      <CountProvider selectedBrand={selectedSupplier} adminView={true}>
        <AdminDashboardContent />
      </CountProvider>
    </div>
  );
};

export default AdminDashboard;
